import {Component, OnInit, ViewChild, ElementRef} from '@angular/core';
import {Agent, AgentConfig} from '../../../classes/agent';
import {ContactFormLocationType} from '../../../classes/enums';
import { Listing } from '../../../classes/listing';
import { DetailStub } from '../../listing-details/detail-section/detail-section.component';
import { PageProfileComponent } from '../page-profile.component';
import { Office } from '../../../classes/office';
import { HistoryItem } from '../../../classes/history-item';
import { RemaxLeadEntity, MapAgentToLead } from '../../../classes/leads';
import { User } from '../../../classes/user';
import { skip } from 'rxjs/operators';
import { AgentRating } from '../../../classes/agentRating';
import { AccordionItemComponent } from '../../../shared/components/accordion/item/accordion-item.component';
import {OAAgentProvider, IndividualAgentAwards} from "../../../app-config.constants";
import {AgentAward, CombinedAwards} from "../../../classes/interfaces";


@Component({
  selector: 'app-agent-profile',
  templateUrl: './agent-profile.component.html',
  styleUrls: ['../page-profile.scss', './agent-profile.desktop.scss', './agent-profile.mobile.scss']
})
export class AgentProfileComponent extends PageProfileComponent implements OnInit {
  agent: Agent = new Agent();
  agentOffice: Office;
  agentListings: Array<Listing> = [];
  leadInfo: Partial<RemaxLeadEntity>;
  ContactFormLocationType = ContactFormLocationType;
  rating: AgentRating;
  favouriteAgentId: string;

  @ViewChild('desktopReviews') desktopReviewsRef: ElementRef;
  @ViewChild('mobileReviews') mobileReviewsRef: AccordionItemComponent;
  @ViewChild('mobileReviews', { read: ElementRef }) mobileReviewsElementRef: ElementRef;

  ngOnInit() {
    super.ngOnInit();
    this.addSub(this.activatedRoute.data.subscribe(({agent, user}: {agent: Agent, user: User}) => {
      this.leadInfo = MapAgentToLead(agent);
      this.setUserDetailsForContactForm(user);
      this._fillAgentDetails(agent);
      this.rankService.getAgentRating(agent.personifyId).toPromise().then(rating => this.rating = rating);
    }));

    // First skipped due to resolver
    this.addSub(this.userService.currentUser.pipe(skip(1)).subscribe((user) => this.setUserDetailsForContactForm(user)));
    this.addSub(this.preferenceServices.favouriteAgentsChanged.subscribe((agents) => {
      this.favouriteAgentId = agents.length ? agents[0] : null
      this.agent.isFavourite = agents.indexOf(this.agent.sourceSystemId) >= 0;
    }));
  }

  private _fillAgentDetails(agent: Agent) {
    this.agent = agent;
    this.isLoading = false;
    this.prerenderService.setPreRenderFlag(200);

    this.agentService.getAgentConfig()
      .then((response: AgentConfig) => {
        this.updateAgentAwards(agent, response);
      });
    // const agentId = this.route.snapshot.params.id;

    this.pushHistory(new HistoryItem({agentId: agent.personifyId, detailUrl: this.agent.detailUrl}));

    this.fastFactRows = [
      new DetailStub({ title: 'page-profile.languages', value: this.agent.languages.join(', ')}),
      new DetailStub({ title: 'page-profile.designations', value: this.agent.designations.join(', ')}),
      new DetailStub({ title: 'page-profile.contact', value: this.agent.primaryNumberToContact ? this.agent.parsedPhoneNumber : ''}),
    ];
    this.getMoreListings();

    this.officeService.getOfficeDetails('' + this.agent.remaxOfficeId, true).then((office) => {
      this.agentOffice = office;
      const translateArgs = {city: this.agentOffice.city, name: this.agent.fullName, brokerage: this.agentOffice.officeName};
      this.metaTagService.setPageTitle('meta-tags.agent-profile.title', translateArgs);
      this.metaTagService.setPageDescription('meta-tags.agent-profile.description', translateArgs);
      this.metaTagService.setOpenGraphTags([
        {
          name: 'title',
          content: 'meta-tags.agent-profile.title',
          variables: translateArgs,
          isTranslateKey: true
        },
        {
          name: 'description',
          content: 'meta-tags.agent-profile.description',
          variables: translateArgs,
          isTranslateKey: true
        },
        {
          name: 'image',
          content: this.agent.associatePhoto
        }
      ]);
      this.getCmsLinks(office.state, office.city);
      // this.getInternalLinks('app', office.state, office.city);
    });
    this.chatService.setAgent(this.agent);
  }

  scrollToContactAgent() {
    if (this.screenSize >= this.SCREEN_SIZE.DESKTOP_UP) {
      this.openContactAgentOrOfficeForm(this.agent);
    } else {
      this.domManipulationService.scrollIntoView(this.contactAgentMobileRef, true);
    }
  }

  scrollToReviews() {
    if (this.screenSize > this.SCREEN_SIZE.MOBILE_ONLY) {
      this.domManipulationService.scrollIntoView(this.desktopReviewsRef, true);
    } else {
      this.domManipulationService.scrollIntoView(this.mobileReviewsElementRef, true);
      this.mobileReviewsRef.shouldShowAccordion = true;
    }
  }

  getMoreListings() {
    this.hasRetrievedSuggestedListings = false;

    this.agentService.getListingsPreviewsForAgent(this.agent.personifyId, this.listingsPagination).then(response => {
      this.hasRetrievedSuggestedListings = true;
      this.listingsPagination.totalResults = response.totalHits;
      this.agentListings.push(...response.results);
      this.productSchema = this.listingsService.checkForProductValues(this.agentListings);
    });
  }

  private updateAgentAwards(agent: Agent, config: AgentConfig) {

    const isOAAgent = agent.sourceSystem === OAAgentProvider;

    if(!agent.combinedAwards || agent.combinedAwards.length === 0 || isOAAgent && config.hideAllAwardsOA || !isOAAgent && config.hideAllAwardsWC ) {
      agent.combinedAwards = [];
      return;
    }

    const yearsToHide = isOAAgent ? config.awardYearsToHideOA : config.awardYearsToHideWC;
    const seriesToHide = isOAAgent ? config.awardSeriesToHideOA : config.awardSeriesToHideWC;

    if(seriesToHide) {
      agent.combinedAwards.forEach(s => s.awards = s.awards.filter(award => !seriesToHide.includes(award.name)));
    }

    if(yearsToHide) {
      agent.combinedAwards.forEach(s => this.trimYearsFromCategory(s, yearsToHide)); // trim years for each category of awards
    }

    agent.combinedAwards.forEach(f => {
      f.awards = f.awards.filter(f => !yearsToHide.includes(f.year));
    });

    agent.combinedAwards = agent.combinedAwards.filter(f => {
        return f.awardCategory === IndividualAgentAwards ? f.awards.some(s => s.years.length > 0) : true;
    });
  }

  private trimYearsFromCategory(s: CombinedAwards, yearsToHide: Number[]) {
    s.awards.forEach(award => { // for each award in the category
      award.years = award.years.filter(year => !yearsToHide.includes(year)); // trim any years that needed to be hidden from award range
      if(award.years && award.years.length === 1) { // this covers the scenario where the years range has a different year from the main year of the award
        award.year = award.years[0].valueOf(); // the number in the year range needs to be shown rather than the main year
      }
    });
  }
}
